import PropTypes from "prop-types";
import React from "react";
import { Button } from "backpack-ui";
import { remove, uniqBy, compact } from "lodash/array";
import { find, each, map } from "lodash/collection";
import debounce from "lodash/debounce";
import shallowCompare from "react-addons-shallow-compare";
import { connect } from "react-redux";
import PageHeader from "backpack-ui/dist/components/pageHeader";
import Breadcrumbs from "./breadcrumbs";
import ContentBlock from "./contentBlock";
import ImagesContainer from "./imagesContainer";
import {
  createListUrl,
  createDetailUrlFromResource,
} from "../../../../server/utils/createUrls";
import { connected as ListItems } from "../../list/components/listItems";

const _ = { debounce, find, each, map, remove, uniqBy };


function convertPoiToLodging(resources, lodgings) {
  return _.map(resources, (resource) => {
    const lodging = _.find(
      lodgings,
      lod => lod.relationships.poi.data.id === resource.id
    );

    if (lodging) {
      lodging.name = resource.name;
      return lodging;
    }

    return resource;
  });
}

/**
 * Renders a non-bookable Collection
 */
class CollectionComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      nearby: false,
      hideMap: false,
      windowWidth: 0,
      adLeft: 0,
      adWidth: 0,
    };

    this.adThreshold = 969;

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.updateDimensionsForAd = this.updateDimensionsForAd.bind(this);
  }

  componentDidMount() {
    if (window.innerWidth > this.adThreshold) {
      this.updateDimensionsForAd();

      window.addEventListener(
        "resize",
        _.debounce(this.updateDimensionsForAd, 250)
      );
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillUnmount() {
    if (window.innerWidth > this.adThreshold) {
      window.removeEventListener("resize", this.updateDimensionsForAd);
    }
  }

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(
      poi,
      breadcrumb.map(b => b.title),
      params.placeId
    );
  }

  updateDimensionsForAd() {
    const maxWidth = 1290;
    const gutter = 30;

    this.setState({
      windowWidth: window.innerWidth,
      adLeft: (maxWidth + gutter) / 4,
      adWidth: maxWidth,
    });
  }

  render() {
    const { poi, breadcrumb, place, images, params, attribution } = this.props;
    const { windowWidth, adLeft, adWidth } = this.state;

    const placeName = (poi.narrativePlace) ? poi.narrativePlace.name : place.name;
    const poiId = `poi-${poi.id}`;

    let categoryLink;
    let categoryName;
    let name;
    const isSleepingNarrative = poi.name === "Sleeping";

    if (isSleepingNarrative) {
      name = `Best places to stay in ${placeName}`;
      categoryName = "Hotels";
      categoryLink = createListUrl({
        resource: "lodging",
        breadcrumb: breadcrumb.map(b => b.title),
        placeId: place.id,
      });
    } else {
      name = `Best ${poi.name} in ${placeName}`;
      categoryName = "Collections";
      categoryLink = createListUrl({
        resource: poi.type,
        kind: poi.poiType,
        breadcrumb: breadcrumb.map(b => b.title),
        placeId: place.id,
      });
    }

    const currentBreadcrumb = {
      title: categoryName,
      href: categoryLink,
    };

    const delimiter = (poi.name === "Sleeping") ? "</li>" : "</p>";
    const intro = _.find(poi.text.split(delimiter), (del) => !del.includes("</a>"));

    let mentionedResources = [];
    _.each(poi.mentions, mention => {
      if (mention.poi) {
        const mentionPoiId = mention.poi.match(/\d+/)[0];
        mentionedResources.push(
          _.find(poi.mentionedPois, innerPoi => innerPoi.id === mentionPoiId)
        );
      }

      if (mention.place) {
        const placeId = mention.place.match(/\d+/)[0];
        mentionedResources.push(
          _.find(
            poi.mentionedPlaces,
            mentionedPlace => mentionedPlace.id === placeId
          )
        );
      }
    });

    mentionedResources = compact(mentionedResources);

    if (poi.name === "Sleeping" && poi.narrativeLodgings) {
      mentionedResources = convertPoiToLodging(mentionedResources, poi.narrativeLodgings);
    }

    _.each(mentionedResources, (resource, index) => {
      resource.review = {
        essential: poi.mentions[index].description,
      };
    });

    const uniqueMentionedResources = _.uniqBy(mentionedResources, "id");

    const adWrapperStyle = {
      left: windowWidth > this.adThreshold
        ? `-${(adLeft / windowWidth) * 100}vw`
        : 0,
      position: "relative",
      textAlign: "center",
      width: windowWidth > this.adThreshold
        ? `${(adWidth / windowWidth) * 100}vw`
        : "100%",
    };

    return (
      <div className="PageContainer" id={poiId} key={poiId}>
        <Breadcrumbs links={[...breadcrumb, currentBreadcrumb]} />

        <div className="Page Page--collection">
          <PageHeader
            heading={name}
            title={categoryName}
            titleHref={categoryLink}
            alignment="center"
            contained
          />

          <div className="PageContent">
            {images &&
              images.length > 0 &&
              this.props.mobile &&
              <ImagesContainer
                images={images}
                attribution={attribution}
                mobile
              />}

            <article className="PageArticle">
              {images &&
                images.length > 0 &&
                !this.props.mobile &&
                <ImagesContainer images={images} attribution={attribution} />}

              <div className="PageArticle-content">

                {mentionedResources.length === 0 && isSleepingNarrative &&
                  <article>
                    <p>
                      Oops, there are no hotels in this collection yet,
                      but you can see all the hotels in {placeName} <a href={categoryLink}>here</a>.
                    </p>
                  </article>
                }

                {intro &&
                  <article>
                    <ContentBlock htmlContent={intro} />
                  </article>
                }

                {mentionedResources.length !== 0 &&
                  <ListItems
                    pois={uniqueMentionedResources}
                    type="poi"
                    params={params}
                    breadcrumbs={breadcrumb}
                    onShowPrices={this.showListPrices}
                    adWrapperStyle={adWrapperStyle}
                  />
                }

                {mentionedResources.length !== 0 && isSleepingNarrative &&
                  <Button
                    color="white"
                    size="medium"
                    href={categoryLink}
                    customStyles={{
                      marginTop: "90px",
                    }}
                    rounded
                    full
                    border
                  >
                    Show more hotels
                  </Button>
                }
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }
}

CollectionComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: PropTypes.object.isRequired,

  /**
   * An array of images
   */
  images: PropTypes.array.isRequired,

  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: PropTypes.bool,

  /**
   * React router params
   */
  params: PropTypes.object,
  /**
   * The breadcrumbs for this page
   */
  breadcrumb: PropTypes.arrayOf(Object),

  attribution: PropTypes.arrayOf(Object),
};

const mapStateToProps = state => ({
  mobile: state.mobile.isMobile,
  breadcrumb: state.breadcrumb,
  resource: state.resource,
  kind: state.kind,
});

export default connect(mapStateToProps)(CollectionComponent);


// WEBPACK FOOTER //
// ./assets/pois/components/collection.jsx